import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getSyncStatus,
  getCachedProducts,
  getCachedClients,
  cacheProducts,
  cacheClients,
  addPendingOrder,
  addPendingClient,
  getPendingOrders,
  getPendingClients,
  removePendingOrder,
  updateLocalClientWithServerId,
  addCachedClient
} from '../services/offlineStorage';
import { calculatorService, clientService } from '../services/api';
import apiService from '../services/api';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    pendingOrders: 0,
    pendingClients: 0,
    hasPendingData: false,
    lastProductsSync: null,
    lastClientsSync: null
  });

  // Products and clients cache in memory for quick access
  const [products, setProducts] = useState({});
  const [clients, setClients] = useState([]);

  // Update sync status
  const refreshSyncStatus = useCallback(async () => {
    const status = await getSyncStatus();
    setSyncStatus(status);
    return status;
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      toast.success('Conexion restaurada', { autoClose: 2000 });
      // Auto-sync when coming back online
      await syncPendingData();
      // Refresh data from server
      await loadData(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Sin conexion - Modo offline', { autoClose: 3000 });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load data on mount
  const loadData = useCallback(async (forceRefresh = false) => {
    try {
      // Skip server refresh if no token to avoid 401 redirect loop
      const hasToken = !!localStorage.getItem('token');

      if (navigator.onLine && forceRefresh && hasToken) {
        // Load from server and cache
        const [productsRes, clientsData] = await Promise.all([
          calculatorService.getProducts(),
          clientService.getAll()
        ]);
        // El endpoint devuelve { success, products: { molduras: [...], cristales: [...] } }.
        // Hay que quedarse con `products`: si se aplana el envoltorio entero, a IndexedDB le
        // llega `true` como si fuera un producto y falla ("Data provided ... does not meet
        // requirements"), con lo que la caché offline de productos nunca llegaba a guardarse.
        const productsData = (productsRes && productsRes.products) ? productsRes.products : productsRes;

        // Cache the categorized format as a single JSON entry for offline use
        const productArray = Object.values(productsData || {})
          .filter(Array.isArray)
          .flat()
          .filter((p) => p && p.id);
        await cacheProducts(productArray);
        // Also cache the categorized format directly
        try {
          localStorage.setItem('iltzar_products_categorized', JSON.stringify(productsData));
        } catch (e) { /* ignore storage errors */ }
        await cacheClients(clientsData);

        setProducts(productsData);
        setClients(clientsData);
        console.log(`Loaded ${productArray.length} products and ${clientsData.length} clients from server`);
      } else {
        // Load from cache - prefer categorized format
        let productsData = {};
        try {
          const cached = localStorage.getItem('iltzar_products_categorized');
          if (cached) {
            productsData = JSON.parse(cached);
          }
        } catch (e) { /* ignore parse errors */ }

        // Fallback to IndexedDB cache if no categorized data
        if (!productsData || Object.keys(productsData).length === 0) {
          const [cachedProducts] = await Promise.all([getCachedProducts()]);
          const productsObj = {};
          cachedProducts.forEach(p => { productsObj[p.id] = p; });
          productsData = productsObj;
        }

        const cachedClients = await getCachedClients();

        setProducts(productsData);
        setClients(cachedClients);
        console.log(`Loaded products and ${cachedClients.length} clients from cache`);

        // If online and have token, refresh in background
        if (navigator.onLine && localStorage.getItem('token')) {
          loadData(true); // Background refresh
        }
      }

      await refreshSyncStatus();
      setIsInitialized(true);
    } catch (error) {
      // Don't log 401 errors (token expired is handled by interceptor)
      if (error?.response?.status !== 401) {
        console.error('Error loading data:', error);
      }
      // Try to load from cache on error
      let productsData = {};
      try {
        const cached = localStorage.getItem('iltzar_products_categorized');
        if (cached) productsData = JSON.parse(cached);
      } catch (e) { /* ignore */ }

      if (!productsData || Object.keys(productsData).length === 0) {
        const cachedProducts = await getCachedProducts();
        cachedProducts.forEach(p => { productsData[p.id] = p; });
      }

      const cachedClients = await getCachedClients();
      setProducts(productsData);
      setClients(cachedClients);
      setIsInitialized(true);
    }
  }, [refreshSyncStatus]);

  // Initialize on mount and auto-sync pending data
  useEffect(() => {
    const init = async () => {
      await loadData();
      // Auto-sync pending data on page load if online
      if (navigator.onLine) {
        const status = await getSyncStatus();
        if (status.hasPendingData) {
          console.log(`Auto-syncing ${status.pendingOrders} orders and ${status.pendingClients} clients...`);
          // Small delay to let the app finish rendering
          setTimeout(() => syncPendingData(), 1500);
        }
      }
    };
    init();
  }, [loadData]);

  // Sync pending clients
  const syncPendingClients = async () => {
    const pendingClients = await getPendingClients();
    const results = { success: 0, failed: 0 };

    for (const pendingClient of pendingClients) {
      try {
        const clientData = {
          name: pendingClient.name,
          type: pendingClient.type || 'particular',
          email: pendingClient.email,
          phone: pendingClient.phone,
          street: pendingClient.street || pendingClient.address,
          city: pendingClient.city,
          zip: pendingClient.zip,
          taxId: pendingClient.taxId || pendingClient.nif,
          marginPercentage: pendingClient.marginPercentage || pendingClient.margin || 100,
          notes: pendingClient.notes
        };

        const serverClient = await apiService.createClient(clientData);
        await updateLocalClientWithServerId(pendingClient.localId, serverClient);
        results.success++;
      } catch (error) {
        console.error('Error syncing client:', error);
        results.failed++;
      }
    }

    return results;
  };

  // Sync pending orders
  const syncPendingOrders = async () => {
    const pendingOrders = await getPendingOrders();
    const results = { success: 0, failed: 0 };
    const cachedClients = await getCachedClients();

    for (const pendingOrder of pendingOrders) {
      try {
        let clientId = pendingOrder.clientId;

        // Resolve local client IDs
        if (clientId && clientId.toString().startsWith('local_')) {
          const syncedClient = cachedClients.find(c =>
            (c.localId === clientId || c.id === clientId) && !c.isLocal
          );
          if (syncedClient) {
            clientId = syncedClient.id;
          } else {
            results.failed++;
            continue;
          }
        }

        const orderData = { ...pendingOrder, clientId };
        delete orderData.localId;
        delete orderData.createdAt;
        delete orderData.syncStatus;

        await calculatorService.saveOrder(orderData);
        await removePendingOrder(pendingOrder.localId);
        results.success++;
      } catch (error) {
        console.error('Error syncing order:', error);
        results.failed++;
      }
    }

    return results;
  };

  // Sync all pending data
  const syncPendingData = async () => {
    if (!navigator.onLine || isSyncing) return;

    setIsSyncing(true);
    try {
      const clientResults = await syncPendingClients();
      const orderResults = await syncPendingOrders();
      await refreshSyncStatus();

      const total = clientResults.success + orderResults.success;
      const failed = clientResults.failed + orderResults.failed;

      if (total > 0) {
        toast.success(`Sincronizados: ${total} elementos`);
      }
      if (failed > 0) {
        toast.warning(`${failed} elementos pendientes de sincronizar`);
      }
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Save order (online or offline)
  const saveOrder = async (orderData) => {
    if (navigator.onLine) {
      try {
        const result = await calculatorService.saveOrder(orderData);
        return { success: true, data: result, offline: false };
      } catch (error) {
        // If online but request fails, queue it
        console.error('Online save failed, queueing:', error);
        const localId = await addPendingOrder(orderData);
        await refreshSyncStatus();
        return { success: true, localId, offline: true };
      }
    } else {
      // Offline - queue the order
      const localId = await addPendingOrder(orderData);
      await refreshSyncStatus();
      toast.info('Pedido guardado localmente', { autoClose: 2000 });
      return { success: true, localId, offline: true };
    }
  };

  // Create client (online or offline)
  const createClient = async (clientData) => {
    if (navigator.onLine) {
      try {
        const result = await apiService.createClient(clientData);
        // Add to local cache
        setClients(prev => [...prev, result]);
        await addCachedClient(result);
        return { success: true, data: result, offline: false };
      } catch (error) {
        console.error('Online client creation failed, queueing:', error);
        const pendingClient = await addPendingClient(clientData);
        setClients(prev => [...prev, { ...pendingClient, id: pendingClient.localId, isLocal: true }]);
        await refreshSyncStatus();
        return { success: true, data: pendingClient, offline: true };
      }
    } else {
      // Offline - queue the client
      const pendingClient = await addPendingClient(clientData);
      setClients(prev => [...prev, { ...pendingClient, id: pendingClient.localId, isLocal: true }]);
      await refreshSyncStatus();
      toast.info('Cliente guardado localmente', { autoClose: 2000 });
      return { success: true, data: pendingClient, offline: true };
    }
  };

  // Refresh data from server
  const refreshData = useCallback(async () => {
    if (navigator.onLine) {
      await loadData(true);
    }
  }, [loadData]);

  const value = {
    isOnline,
    isSyncing,
    isInitialized,
    syncStatus,
    products,
    clients,
    saveOrder,
    createClient,
    syncPendingData,
    refreshSyncStatus,
    refreshData
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
